<template>
  <popup-dialog
    :visible="visible"
    :title="title"
    :width="500"
    :close-button-visible="true"
    class="popup-save"
    @close="cancel"
  >
    <div class="popup-save__content">
      <p v-if="isSimulationGame">
        {{ $t("messages.gameRestrictions") }}
      </p>
      <form class="column" @submit.prevent="onSubmit()">
        <input-text
          v-model:value="name"
          :label="$t('labels.name')"
          :required="true"
          :autofocus="true"
          :hide-errors="triedToSubmit == false"
          autocomplete="new-model-name"
          data-lpignore="true"
        />
        <input-text
          v-if="isSimulationGame && authorEditable"
          v-model:value="author"
          :label="$t('labels.author')"
          :required="true"
        />
        <div v-else-if="isSimulationGame" class="mb-3">
          <div class="label">{{ $t("labels.author") }}</div>
          {{ author }}
        </div>

        <input-workspace-folder
          v-if="showWorkspaceFolderInput"
          v-model:workspace-id="workspace"
          v-model:folder-id="folder"
          @update:folder-slug="onFolderSlugUpdate"
          @update:workspace-id="onWorkspaceUpdate"
        />
        <input-checkbox
          v-model:value="usePassword"
          :label="$t(`labels.usePasswordProtection`)"
          :label-checked="$t('labels.passwordProtectionActive')"
          class="mt-4"
        />

        <template v-if="usePassword">
          <input-password
            v-if="usePassword"
            v-model:value="password"
            autocomplete="current-model-password"
            :label="$t('labels.password')"
            :required="true"
          />

          <p>
            {{ $t("messages.customPasswordWarning") }}
          </p>
        </template>

        <div class="line justify-center gap-4 mt-2">
          <button class="button button_secondary" type="button" @click="cancel()">
            {{ $t("labels.abort") }}
          </button>
          <button class="button" type="submit" @click="triedToSubmit = true">
            {{ $t("labels.save") }}
          </button>
        </div>
      </form>
    </div>
  </popup-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useSavePopup } from "./SavePopup.js";

import PopupDialog from "@core/ui/PopupDialog.vue";
import InputText from "@core/input/InputText.vue";
import InputPassword from "@core/input/InputPassword.vue";
import InputCheckbox from "@core/input/InputCheckbox.vue";
import InputWorkspaceFolder from "@core/input/InputWorkspaceFolder.vue";
import { showAlert, showMessage } from "@core/ui/alert/alert";

const { t } = useI18n();

const {
  asFileExport,
  visible,
  showWorkspaceFolderInput,
  isSimulationGame,
  name,
  author,
  authorEditable,
  usePassword,
  password,
  workspace,
  folder,
  folderSlug,
  submit,
  cancelSave
} = useSavePopup();

const triedToSubmit = ref(false);
watch(visible, () => {
  triedToSubmit.value = false;
});

const title = computed(() => {
  if (asFileExport.value) {
    return t("labels.export");
  } else if (isSimulationGame.value) {
    return t("labels.saveAsGame");
  } else {
    return t("labels.saveAs");
  }
});

function cancel() {
  cancelSave();
}
async function onSubmit() {
  if (showWorkspaceFolderInput.value) {
    if (workspace?.value == null) {
      showMessage(t("messages.chooseWorkspace"));
      return;
    }
    if (folder?.value == null) {
      showMessage(t("messages.chooseFolder"));
      return;
    }
  }

  if (usePassword.value) {
    // double-opt-in:
    // should we REALLY set a password?
    const shouldSave = await showAlert({
      title: t("questions.sure"),
      denyText: t("labels.abort"),
      confirmText: t("labels.save"),
      content: [
        {
          type: "text",
          text: t("messages.customPasswordWarning")
        }
      ]
    });

    // restart saving prompt process if user decided against password
    if (!shouldSave) {
      return;
    }
  }

  submit();
}
function onWorkspaceUpdate(val: number) {
  if (!workspace) return;
  workspace.value = val;
}
function onFolderSlugUpdate(val: string) {
  if (!folderSlug) return;
  folderSlug.value = val;
}
</script>

<style lang="scss">
.popup-save {
  overflow: visible;

  &__content {
    padding: 2rem;
  }
}
</style>
